import { useState } from 'react';
import { chefPlating, Modal, SnackbarStatus, Typography, useSnackbar } from '@weareopenr/component-library';
import { GlobalItemTypeForm } from './GlobalItemTypeForm/GlobalItemTypeForm';
import { GlobalItemDetailsForm } from './GlobalItemDetailsForm/GlobalItemDetailsForm';
import { GlobalItemTaxRateForm } from './GlobalItemTaxRateForm/GlobalItemTaxRateForm';
import { ICreateGlobalItemWizardProps, IGlobalItem, Steps } from './types';
import styles from './CreateGlobalItemWizard.module.scss';
import { formatGlobalItemPayload } from './utilities';
import { createGlobalItem } from '../../api/createGlobalItem';
import { useNavigate } from 'react-router-dom';
import { FormVariant } from '../../types/menuItemForm.types';
import { GlobalItemBundleCategory } from './GlobalItemBundleCategory/GlobalItemBundleCategory';
import { createModifierGroup } from '../../api/createModifierGroup/createModifierGroup';
import { IOptionGroupPayload } from '../../api/updateModifierGroup/types';

import { useSelectedPriceScheme } from 'src/hooks/useSelectedPriceScheme/useSelectedPriceScheme';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { GlobalItemBundlePricing } from './GlobalItemBundlePricing/GlobalItemBundlePricing';
import { addItemsToMenu } from '../../api/addItemsToMenu';

const GlobalItemWizard = ({ closeModal, menuId, sectionId }: Omit<ICreateGlobalItemWizardProps, 'open'>) => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { trackEvent, captureException } = useUserTracking();
  const [globalItem, setGlobalItem] = useState<Partial<IGlobalItem>>({});
  const [step, setStep] = useState<Steps>(Steps.type);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { selectedPriceSchemeId } = useSelectedPriceScheme();
  const variant = FormVariant.CREATE;
  const submitStep = globalItem.type === 'BUNDLE' ? Steps.bundlePricing : Steps.tax;
  const totalSteps = submitStep + 1;

  const updateGlobalItem = (data: Partial<IGlobalItem>) => {
    setGlobalItem((prev) => ({ ...prev, ...data }));
  };

  const createItem = async (data: Partial<IGlobalItem>) => {
    setIsSubmitting(true);
    try {
      const payload = formatGlobalItemPayload(data as IGlobalItem);
      const newItemId = (await createGlobalItem(payload)).item_id;
      const additionalItemRequests = [];

      if (data.type === 'BUNDLE') {
        const bundleGroup = data.modifier_groups[0];
        const reqBody: IOptionGroupPayload = {
          name: bundleGroup.name,
          description: bundleGroup.description,
          is_repeatable: true,
          max_choices: bundleGroup.max_choices,
          min_choices: bundleGroup.min_choices,
          modifiers: bundleGroup.modifiers.map((mod) => ({
            item_id: mod.optionItemId,
            plu: mod.inheritPosId ? null : mod.posId || null,
            value_with_tax: mod.useDefaultPrice ? null : mod.valueWithTax || 0,
          })),
        };

        additionalItemRequests.push(createModifierGroup(newItemId, reqBody, selectedPriceSchemeId));
      }
      await Promise.all(additionalItemRequests);

      if (menuId) {
        await addItemsToMenu(menuId, [
          {
            item_id: newItemId,
            section_id: sectionId ?? null,
          },
        ]);
      }
      trackEvent('product_created', { productId: newItemId, productName: data.name, productType: data.type });
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: `You have successfully created ${data.name}.`,
      });
      navigate(`/items/${newItemId}`);
    } catch (e) {
      captureException(e, { tags: { action: 'create_product', productName: data.name, productType: data.type } });
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: 'Something went wrong whilst creating a product',
      });
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Typography className={styles['wizard-steps']}>{`Step ${step + 1} of ${totalSteps}`}</Typography>

      {step === Steps.type && (
        <GlobalItemTypeForm
          variant={variant}
          setStep={setStep}
          setGlobalItem={updateGlobalItem}
          onCancel={closeModal}
          defaultType={globalItem.type}
        />
      )}

      {step === Steps.details && (
        <GlobalItemDetailsForm
          variant={variant}
          item={globalItem}
          onCloseOrBack={setStep}
          setGlobalItem={updateGlobalItem}
          showImageField
        />
      )}
      {step === Steps.tax && (
        <GlobalItemTaxRateForm
          variant={variant}
          item={globalItem}
          isSubmitting={isSubmitting}
          onSubmitItem={submitStep === Steps.tax && createItem}
          onCloseOrBack={setStep}
          setGlobalItem={updateGlobalItem}
        />
      )}
      {step === Steps.bundleCategory && (
        <GlobalItemBundleCategory
          wizardItem={globalItem}
          onBackOrClose={() => setStep(Steps.tax)}
          onContinue={() => setStep(Steps.bundlePricing)}
          setGlobalItem={updateGlobalItem}
        />
      )}
      {step === Steps.bundlePricing && (
        <GlobalItemBundlePricing
          wizardOnSubmit={createItem}
          wizardItem={globalItem}
          goBack={() => setStep(Steps.bundleCategory)}
          isSubmitting={isSubmitting}
        />
      )}
    </>
  );
};

export const CreateGlobalItemWizard = ({ open, closeModal, menuId, sectionId }: ICreateGlobalItemWizardProps) => {
  return (
    <Modal image={chefPlating} open={open} closeModal={closeModal}>
      <GlobalItemWizard closeModal={closeModal} menuId={menuId} sectionId={sectionId} />
    </Modal>
  );
};
